import {ChangeDetectorRef, Injectable} from "@angular/core";
import {ContentCursor} from "../content/content.cursor";
import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {Observable} from "rxjs";
import {Observer} from "rxjs";
import {Subject} from "rxjs";
import {CssSentence, CssText} from "../../content/content-view.objects";

@Injectable()
export class ScrollService {

    private static SCROLL_DURATION: number = 500;
    public _scrollTextObservable: Observable<any>;
    private scrollTextObserver: Observer<any>;
    private scrollAllowed: boolean = true;
    private scrolling: boolean = false;
    private _changeDetectorRef: ChangeDetectorRef = null;

    constructor(private contentCursor: ContentCursor,
                private log: BugsnagLoggingService) {
        this._scrollTextObservable = new Observable(observer => {
            this.scrollTextObserver = observer;
        });
    }

    // scrolling text
    public checkIfSentenceInView(sentence: CssSentence): boolean {
        // check if this upcoming sentence goes below the view, and also make sure that it is not "under" the title.
        const thisSentenceBottom = sentence.getRectBottom() - this.getYloc();
        const thisSentenceTop = sentence.getRectTop() - this.getYloc();
        const selHeight: number = this.getHeight();
        this.log.log("checkIfSentenceInView: sentenceBottom: " + thisSentenceBottom + " height: " + selHeight);

        return selHeight > thisSentenceBottom && thisSentenceBottom >= 0 && thisSentenceTop >= this.getTitleHeight();
    }

    // TODO if we are not going to do something different here (e.g. allow not all of image to be showing)
    // could look at combining with checkIfSentence...
    public checkIfImageInView(text: CssText): boolean {
        // check if the image goes below the view.
        let thisImageBottom = text.getRectBottom() - this.getYloc();
        let selHeight: number = this.getHeight();
        return selHeight > thisImageBottom && thisImageBottom >= 0;
    }

    public checkIfSubmitInView(): boolean {
        const submitElement =  document.getElementById("question-submit");
        let submitBottom = !!submitElement ? this.getEndPosition(submitElement) : 0;
        return this.getEndPosition(this.getElement()) >= submitBottom;
    }

    private calculateAdditionalScroll(sentence: CssSentence, currentScroll: number): number {
        // may need to scroll further if the next sentence will not be in-view after we scroll to an image
        if (!sentence) {
            return 0;
        }
        let thisSentenceBottom = sentence.getRectBottom() - this.getYloc() - currentScroll;
        thisSentenceBottom = thisSentenceBottom < 0 ? 0 : thisSentenceBottom;
        let selHeight: number = this.getHeight();
        return thisSentenceBottom - selHeight > 0 ? thisSentenceBottom - selHeight : 0;
    }

    public getTitleHeight(): number {
        return document.getElementById("selection-title").getBoundingClientRect().height;
    }

    // calculations for scrolling
    public getElement(): HTMLElement {
        return document.getElementById("content-wrapper");
    }

    public getWordWallElement(): HTMLElement {
        return document.getElementById("wordwall-wrapper");
    }

    public get scrollTextObservable(): Observable<any> {
        return this._scrollTextObservable;
    }

    public getHeight(): number {
        return this.getElement().getBoundingClientRect().height;
    }

    public getYloc(): number {
        return this.getElement().getBoundingClientRect().top;
    }

    public getBeginPosition(element: any): any {
        return element.getBoundingClientRect().top;
    }

    public getEndPosition(element: any): any {
        return element.getBoundingClientRect().bottom;
    }

    public getVerticalCollideInfo(topElement: any, bottomElement: any): any {
        let topElementPosition: number = this.getEndPosition(topElement);
        let bottomElementPosition: number = this.getBeginPosition(bottomElement);
        let buffer: number = topElementPosition - bottomElementPosition;
        return { isColliding: buffer > 0, buffer: buffer };
    }

    public set changeDetectorRef(ref: ChangeDetectorRef) {
        this._changeDetectorRef = ref;
    }

    async scrollText(): Promise<void>  {
        // need to calculate how far to scroll; it's either a page height, or to the end of the selection
        let sentence = this.contentCursor.selectedSentence;
        let previousText = this.contentCursor.selection.previousText(this.contentCursor.selectedText);
        let titleHeight: number = this.getTitleHeight();
        // calculate the target scroll position, taking into account showing images in the reading, and the title height
        let scrollToNextPosition = this.getNextScrollPosition(previousText, sentence, titleHeight);
        let viewHt: number = this.getHeight();
        let scrollHt: number = this.getElement().scrollHeight;
        let scrollToEnd: number = scrollHt - viewHt;
        // either scroll to the target position or to end, whichever is less
        let targetY: number = Math.min(scrollToNextPosition, scrollToEnd);
        this.log.log("scrollText: viewHt: " + viewHt + " scrollHt: " + scrollHt + "  scrollToNextSentence: "
            + scrollToNextPosition + " scrollToEnd: " + scrollToEnd);
        this.log.log("scrollText: targetY: " + targetY + " curScrollTop: " + this.getElement().scrollTop);
        let elem: HTMLElement = this.getElement();
        // await when this.doScroll is done, then notify so goFromSelectedSentence can start reading again.
        await this.doScroll(elem, targetY);
        if (!!this.scrollTextObserver) {
            this.scrollTextObserver.complete();
        }
    }

    async scrollWordWallToEnd(): Promise<void> {
        let elem: HTMLElement = this.getWordWallElement();
        // scroll to bottom so that button is visible
        let targetY = this.getScrollToEnd(elem);
        // observe when scrollTo is done
        await this.doScroll(elem, targetY);
    }

    async scrollTextToEnd(): Promise<void> {
        let elem: HTMLElement = this.getElement();
        // scroll to bottom so that any final image is visible
        let targetY = this.getScrollToEnd(elem);
        // observe when scrollTo is done
        await this.doScroll(elem, targetY);
    }

    public getScrollToEnd(elem: HTMLElement): number {
        let viewHt: number = elem.getBoundingClientRect().height;
        let scrollHt: number = elem.scrollHeight;
        let targetY: number = scrollHt - viewHt;
        this.log.log("scroll: viewHt:" + viewHt + "targetY: " + targetY + " scrollHt: " + scrollHt);
        return targetY;
    }

    public getNextScrollPosition(previousText: CssText, sentence: CssSentence, titleHeight: number): number {
        let scrollToNextPosition;
        let curScrollTop: number = this.getElement().scrollTop;
        // subtracting out titleHeight is needed to avoid text or image ending up "under" the title.
        if (previousText && previousText.isImage() && !this.checkIfImageInView(previousText)) {
            // if the next thing outside of current view is an image, scroll so that image is at the top of the view,
            // rather than scrolling past it
            let imageTop: number = previousText.getRectTop() - this.getYloc();
            let scrollDelta = imageTop - titleHeight;
            // however there must at least be one sentence in view for recording and read to me.
            // calculateAdditionalScroll gives the additional amount to ensure that, if needed.
            scrollToNextPosition = scrollDelta + curScrollTop +
                this.calculateAdditionalScroll(sentence, scrollDelta);
        } else {
            // getRectTop is in relation to the top of the viewport
            let nextSentenceTop: number = (sentence ? sentence.getRectTop() : 0) - this.getYloc();
            scrollToNextPosition = nextSentenceTop + curScrollTop - titleHeight;
        }
        return scrollToNextPosition;
    }

    private async doScroll(elem: HTMLElement, targetY: number): Promise<void> {
        this.scrolling = true;
        if (this._changeDetectorRef) {
            this._changeDetectorRef.detach();
        }
        await this.scrollTo(elem, targetY, ScrollService.SCROLL_DURATION);
        if (this._changeDetectorRef) {
            this._changeDetectorRef.reattach();
            this._changeDetectorRef.detectChanges();
        }
        this.scrolling = false;
    }

    public disableScroll(): void {
        this.scrollAllowed = false;
    }

    public enableScroll(): void {
        this.scrollAllowed = true;
    }

    public isScrollingAllowed(): boolean {
        return this.scrollAllowed;
    }

    public isCurrentScrolling(): boolean {
        return this.scrolling;
    }

    public scrollTo(element: HTMLElement, to: number, duration: number): Observable<void> {

        let scrollSubject:Subject<void> = new Subject<void>();
        let start = element.scrollTop,
            change = to - start,
            increment = 10;

        // If element.scrollTop is greater than "to", we need to swim back up...
        if (start < to) {
            change = (start - to ) * -1;
        }

        let animateScroll = (elapsedTime) => {
            elapsedTime += increment;
            element.scrollTop = this.easeInOut(elapsedTime, start, change, duration);
            if (this._changeDetectorRef) {
                this._changeDetectorRef.reattach();
                this._changeDetectorRef.detectChanges();
                this._changeDetectorRef.detach();
            }
            if (elapsedTime < duration) {
                setTimeout(() => {
                    animateScroll(elapsedTime);
                }, increment);
            } else {
                // if it's equal, then need to notify Observer
                if (this._changeDetectorRef) {
                    this._changeDetectorRef.reattach();
                    this._changeDetectorRef.detectChanges();
                }
                scrollSubject.complete();
            }
        };

        animateScroll(0);
        return scrollSubject.asObservable();
    }

    public easeInOut(currentTime, start, change, duration): number {
        currentTime /= duration / 2;
        if (currentTime < 1) {
            return change / 2 * currentTime * currentTime + start;
        }
        currentTime -= 1;
        return -change / 2 * (currentTime * (currentTime - 2) - 1) + start;
    }

}
